import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import { getDashboardStats } from '../../services/adminApi';
import { useAuth } from '../../context/AuthContext';
import { isSuperAdmin, isBrandAdmin, isBranchAdmin } from '../../utils/portalRoles';
import { ROUTES } from '../../config/constants';
import './AdminDashboard.css';

const activateOnKey = (e, action) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    action();
  }
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalSalons: 0,
    salonsThisMonth: 0,
    totalMirrors: 0,
    mirrorsThisMonth: 0,
    activeServices: 0,
    totalRevenue: 0,
    revenueThisMonth: 0,
    revenueLastMonth: 0,
    revenueChange: null,
    revenueScope: 'all',
    totalCustomers: 0,
    completedBookings: 0,
    totalFeedback: 0
  });
  const [loading, setLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    setStatsError(null);
    try {
      const response = await getDashboardStats();
      // Support both direct body and wrapped { data: { ... } }
      const raw = response?.data;
      const data = raw && typeof raw === 'object' && 'totalSalons' in raw ? raw : raw?.data || {};
      const num = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : Number(v) || 0);
      setStats({
        totalSalons: num(data.totalSalons),
        salonsThisMonth: num(data.salonsThisMonth),
        totalMirrors: num(data.totalMirrors),
        mirrorsThisMonth: num(data.mirrorsThisMonth),
        activeServices: num(data.activeServices),
        totalRevenue: num(data.totalRevenue),
        revenueThisMonth: num(data.revenueThisMonth),
        revenueLastMonth: num(data.revenueLastMonth),
        revenueChange:
          data.revenueChange == null || data.revenueChange === ''
            ? null
            : num(data.revenueChange),
        revenueScope: data.revenueScope === 'branch' || data.revenueScope === 'brand' ? data.revenueScope : 'all',
        totalCustomers: num(data.totalCustomers),
        completedBookings: num(data.completedBookings),
        totalFeedback: num(data.totalFeedback)
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setStatsError(error.response?.data?.message || error.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout pageTitle="Dashboard">
        <Loader variant="dark" fullScreen={false} message="Loading dashboard..." />
      </AdminLayout>
    );
  }

  const userName = user?.name || user?.profile?.name || user?.email || 'Admin';
  const welcomeScope = isBranchAdmin(user?.role)
    ? 'branch'
    : isBrandAdmin(user?.role)
      ? 'brand'
      : 'branch network';
  const revenueScopeLabel =
    stats.revenueScope === 'branch'
      ? 'this branch'
      : stats.revenueScope === 'brand'
        ? 'this brand'
        : 'all branches';
  const servicesScopeLabel = isBranchAdmin(user?.role)
    ? 'In this branch'
    : isBrandAdmin(user?.role)
      ? 'In this brand'
      : 'Across all branches';

  return (
    <AdminLayout pageTitle="Dashboard">
      <div className="dashboard-welcome">
        <h2 className="welcome-title">Hi, {userName}</h2>
        <p className="welcome-subtitle">Welcome back! Here&apos;s what&apos;s happening with your {welcomeScope}.</p>
      </div>

      {statsError && (
        <div className="dashboard-stats-error">
          {statsError}
          <button type="button" className="btn-retry" onClick={fetchDashboardStats}>Retry</button>
        </div>
      )}

      <div className="dashboard-stats">
        {/* Total Branches Card */}
        <div className="stat-card stat-card-clickable" role="button" tabIndex={0} onClick={() => navigate(ROUTES.ADMIN_SALONS)} onKeyDown={(e) => activateOnKey(e, () => navigate(ROUTES.ADMIN_SALONS))}>
          <div className="stat-header">
            <span className="stat-label">Total Branches</span>
            <div className="stat-icon" style={{ background: '#392200' }}>
              <div className="icon-inner" style={{ background: '#DA7F00' }}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M3 21H21M3 8H21M5 21V8M19 21V8M12 21V8M8 3V8M16 3V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            </div>
          </div>
          <div className="stat-value">
            <span className="stat-main">{stats.totalSalons}</span>
            {stats.totalSalons === 0 ? (
              <span className="stat-increment stat-next-step">Next: add your first branch</span>
            ) : stats.salonsThisMonth > 0 ? (
              <span className="stat-increment">+{stats.salonsThisMonth} branches added this month</span>
            ) : null}
          </div>
        </div>

        {/* Total Mirrors Card */}
        <div className="stat-card stat-card-clickable" role="button" tabIndex={0} onClick={() => navigate(ROUTES.ADMIN_MIRRORS)} onKeyDown={(e) => activateOnKey(e, () => navigate(ROUTES.ADMIN_MIRRORS))}>
          <div className="stat-header">
            <span className="stat-label">Total Mirrors</span>
            <div className="stat-icon" style={{ background: '#001845' }}>
              <div className="icon-inner" style={{ background: '#2D68D5' }}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M3 9H21M9 3V21" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
            </div>
          </div>
          <div className="stat-value">
            <span className="stat-main">{stats.totalMirrors}</span>
            {stats.totalMirrors === 0 ? (
              <span className="stat-increment stat-next-step">Next: register a mirror</span>
            ) : stats.mirrorsThisMonth > 0 ? (
              <span className="stat-increment">+{stats.mirrorsThisMonth} this month</span>
            ) : null}
          </div>
        </div>

        {/* Active Services Card */}
        <div className="stat-card stat-card-clickable" role="button" tabIndex={0} onClick={() => navigate(ROUTES.ADMIN_SERVICES)} onKeyDown={(e) => activateOnKey(e, () => navigate(ROUTES.ADMIN_SERVICES))}>
          <div className="stat-header">
            <span className="stat-label">Active Services</span>
            <div className="stat-icon" style={{ background: '#280039' }}>
              <div className="icon-inner" style={{ background: '#731EC2' }}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor" stroke="currentColor" strokeWidth="2"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
            </div>
          </div>
          <div className="stat-value">
            <span className="stat-main">{stats.activeServices}</span>
            {stats.activeServices === 0 ? (
              <span className="stat-increment stat-next-step">Next: add or activate a service</span>
            ) : (
              <span className="stat-increment">{servicesScopeLabel}</span>
            )}
          </div>
        </div>

        {/* Total Revenue Card */}
        <div className="stat-card stat-card-clickable" role="button" tabIndex={0} onClick={() => navigate(ROUTES.ADMIN_TRANSACTIONS)} onKeyDown={(e) => activateOnKey(e, () => navigate(ROUTES.ADMIN_TRANSACTIONS))}>
          <div className="stat-header">
            <span className="stat-label">Total Revenue</span>
            <div className="stat-icon" style={{ background: '#001845' }}>
              <div className="icon-inner" style={{ background: '#2D68D5' }}>
                <span className="stat-rupee-symbol" aria-hidden="true">₹</span>
              </div>
            </div>
          </div>
          <div className="stat-value">
            <span className="stat-main">₹{stats.totalRevenue.toLocaleString('en-IN')}</span>
            {stats.revenueLastMonth > 0 && stats.revenueChange != null ? (
              <span className={`stat-increment ${stats.revenueChange >= 0 ? 'text-positive' : 'text-negative'}`}>
                {stats.revenueChange >= 0 ? '+' : ''}
                {stats.revenueChange}% vs last month for {revenueScopeLabel} (₹
                {stats.revenueThisMonth.toLocaleString('en-IN')} this month, ₹
                {stats.revenueLastMonth.toLocaleString('en-IN')} last month)
              </span>
            ) : stats.revenueThisMonth > 0 ? (
              <span className="stat-increment">
                ₹{stats.revenueThisMonth.toLocaleString('en-IN')} this month for {revenueScopeLabel}
              </span>
            ) : (
              <span className="stat-increment stat-next-step">
                Next: complete a paid booking for {revenueScopeLabel}
              </span>
            )}
          </div>
        </div>

        {/* Total Customers Card */}
        <div className="stat-card stat-card-clickable" role="button" tabIndex={0} onClick={() => navigate(ROUTES.ADMIN_USERS)} onKeyDown={(e) => activateOnKey(e, () => navigate(ROUTES.ADMIN_USERS))}>
          <div className="stat-header">
            <span className="stat-label">Total Customers</span>
            <div className="stat-icon" style={{ background: '#001845' }}>
              <div className="icon-inner" style={{ background: '#2D68D5' }}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
          <div className="stat-value">
            <span className="stat-main">{stats.totalCustomers}</span>
            {stats.totalCustomers === 0 ? (
              <span className="stat-increment stat-next-step">Next: customers appear after mirror check-in</span>
            ) : (
              <span className="stat-increment">registered</span>
            )}
          </div>
        </div>

        {/* Completed Bookings Card */}
        <div className="stat-card stat-card-clickable" role="button" tabIndex={0} onClick={() => navigate(ROUTES.ADMIN_REPORTS)} onKeyDown={(e) => activateOnKey(e, () => navigate(ROUTES.ADMIN_REPORTS))}>
          <div className="stat-header">
            <span className="stat-label">Completed Bookings</span>
            <div className="stat-icon" style={{ background: '#280039' }}>
              <div className="icon-inner" style={{ background: '#731EC2' }}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M9 12H15M9 16H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            </div>
          </div>
          <div className="stat-value">
            <span className="stat-main">{stats.completedBookings}</span>
            {stats.completedBookings === 0 ? (
              <span className="stat-increment stat-next-step">Next: finish a booking on a mirror</span>
            ) : (
              <span className="stat-increment">total</span>
            )}
          </div>
        </div>

        {/* Total Feedback Card */}
        <div className="stat-card stat-card-clickable" role="button" tabIndex={0} onClick={() => navigate(ROUTES.ADMIN_FEEDBACK)} onKeyDown={(e) => activateOnKey(e, () => navigate(ROUTES.ADMIN_FEEDBACK))}>
          <div className="stat-header">
            <span className="stat-label">Total Feedback</span>
            <div className="stat-icon" style={{ background: '#392200' }}>
              <div className="icon-inner" style={{ background: '#DA7F00' }}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
          <div className="stat-value">
            <span className="stat-main">{stats.totalFeedback}</span>
            {stats.totalFeedback === 0 ? (
              <span className="stat-increment stat-next-step">Next: collect feedback after a session</span>
            ) : (
              <span className="stat-increment">reviews</span>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions Section */}
      <div className="dashboard-quick-actions">
        <div
          className="quick-action-card"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`${ROUTES.ADMIN_MIRRORS}?new=1`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`${ROUTES.ADMIN_MIRRORS}?new=1`);
            }
          }}
        >
          <div className="quick-action-icon" style={{ background: '#1D2747' }}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 5V19M5 12H19" stroke="#4B92FF" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="quick-action-content">
            <div className="quick-action-title">Add Mirror</div>
            <div className="quick-action-subtitle">Deploy new device</div>
          </div>
          <div className="quick-action-arrow">→</div>
        </div>

        {isSuperAdmin(user?.role) && (
        <div
          className="quick-action-card"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`${ROUTES.ADMIN_SERVICES}?new=1`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`${ROUTES.ADMIN_SERVICES}?new=1`);
            }
          }}
        >
          <div className="quick-action-icon" style={{ background: '#1D2747' }}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M7 10L12 15L17 10M12 15V3" stroke="#4B92FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="quick-action-content">
            <div className="quick-action-title">Add Service</div>
            <div className="quick-action-subtitle">Create or bulk import</div>
          </div>
          <div className="quick-action-arrow">→</div>
        </div>
        )}

        <div
          className="quick-action-card"
          role="button"
          tabIndex={0}
          onClick={() => navigate(ROUTES.ADMIN_REPORTS)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(ROUTES.ADMIN_REPORTS);
            }
          }}
        >
          <div className="quick-action-icon" style={{ background: '#1D2747' }}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 17V7M9 7L4 12M9 7L14 12M15 7V17M15 17L20 12M15 17L10 12" stroke="#4B92FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="quick-action-content">
            <div className="quick-action-title">View Report</div>
            <div className="quick-action-subtitle">Analytics & Insights</div>
          </div>
          <div className="quick-action-arrow">→</div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
